'use client';

import { createContext, useContext } from 'react';

import { cn } from '@utils/cn';
import { type SectionStoryblok } from '@utils/storyblok/storyblok';
import { ClientStoryblokComponent } from '@common/storyblok/ClientStoryblokComponent';

import { Container } from './Container';

type SectionProps = SectionStoryblok & {
  className?: string;
};

export type ContainerSizes =
  | 'max-w-7xl'
  | 'max-w-5xl'
  | 'max-w-4xl'
  | 'max-w-3xl'
  | 'max-w-xl'
  | 'max-w-none';

type SectionContextType = {
  color: string | null;
  containerSize: ContainerSizes;
  name: string | undefined;
};

const SectionContext = createContext<SectionContextType>({
  color: null,
  containerSize: 'max-w-7xl',
  name: undefined,
});

export function useSection() {
  return useContext(SectionContext);
}

/** Color → background + foreground mapping aligned with global.css tokens:
 *  primary   = #726a64  (warm taupe)
 *  secondary = #6f6b4f  (accessible olive)
 *  tertiary  = #4f4944  (dark charcoal — the CSS `accent` token)
 */
const colorBgClass: Record<Exclude<SectionStoryblok['color'], '' | undefined>, string> = {
  primary: 'bg-primary text-light',
  secondary: 'bg-secondary text-light',
  tertiary: 'bg-accent text-light',
  background: 'bg-muted',
};

export function Section({
  blocks,
  className,
  color,
  gap,
  marginBottom,
  marginTop,
  marginSize,
  maxWidth,
  name,
}: SectionProps) {
  let containerSize: ContainerSizes = 'max-w-7xl';
  if (maxWidth === 'sm') {
    containerSize = 'max-w-xl';
  } else if (maxWidth === 'md') {
    containerSize = 'max-w-4xl';
  } else if (maxWidth === 'lg') {
    containerSize = 'max-w-7xl';
  } else if (maxWidth === 'false') {
    containerSize = 'max-w-none';
  }

  const normalizedGap =
    typeof gap === 'number' ? gap : typeof gap === 'string' ? Number.parseFloat(gap) : 0;

  const hasColor = Boolean(color && color in colorBgClass);

  return (
    <SectionContext.Provider value={{ color: color || null, containerSize, name }}>
      <div
        className={cn(
          'relative',

          // Apply background + text color when a color is chosen
          hasColor && colorBgClass[color as string],

          // Vertical padding
          'py-16 md:py-24',

          // Margin modifiers (only when no solid background color)
          !hasColor && marginTop && 'mt-8 md:mt-48',
          !hasColor && marginBottom && 'mb-8 md:mb-48',
          !hasColor && marginTop && marginSize === 'sm' && 'mt-4 md:mt-20',
          !hasColor && marginBottom && marginSize === 'sm' && 'mb-4 md:mb-20',
          !hasColor && marginTop && marginSize === 'xs' && 'mt-4 md:mt-4',
          !hasColor && marginBottom && marginSize === 'xs' && 'mb-4 md:mb-4',

          !marginBottom && 'mb-0',
          !marginTop && 'mt-0',
        )}
      >
        <Container
          aria-label={name}
          className={cn(containerSize, 'flex flex-col px-5 sm:px-6 lg:px-8', className)}
          data-bg={color}
          style={normalizedGap > 0 ? { gap: `${normalizedGap * 0.25}rem` } : undefined}
        >
          {blocks?.map((blok: any) => {
            return (
              <ClientStoryblokComponent
                blok={blok}
                key={blok._uid}
              />
            );
          })}
        </Container>
      </div>
    </SectionContext.Provider>
  );
}
